import React, {useState, useEffect} from "react";
import {
    Modal,
    FormGroup,
    FormControl,
    ControlLabel,
    Button
} from "react-bootstrap";
import { View } from "react-native";
import { addDaysToDate, addHoursToDate, addMinutesToDate, filterMonitorsByMonitorId, getMonitorsGroupById, getUser, getLatestAllowedFootageEndTimeWithOffset, FOOTAGE_BUFFER_MINUTES } from "../utils/Common";
import { getMonitors, getLatestStoredImages, makeTransferRequest } from "../utils/bedrockAPI";
import { TimePicker, DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import styled from 'styled-components';
import "./FootageModal.css";
import LoaderButton from "./LoaderButton";

const CustomTimePicker = styled(TimePicker)`
    & .MuiInputBase-input {
        font-family: var(--brand-font);
        color: var(--brand-slate);
        font-size: 24px;
        border: 1px solid var(--brand-border);
        padding: 5px;
        cursor: pointer;
        text-align: center;
        border-radius: var(--brand-radius);
    }
    
    & .MuiInputBase-input:hover {
        background-color: var(--brand-limestone);
    }
`;

const CustomDatePicker = styled(DatePicker)`
    & .MuiInputBase-input {
        font-family: var(--brand-font);
        color: var(--brand-slate);
        font-size: 24px;
        border: 1px solid var(--brand-border);
        padding: 5px;
        cursor: pointer;
        text-align: center;
        border-radius: var(--brand-radius);
    }
    
    & .MuiInputBase-input:hover {
        background-color: var(--brand-limestone);
    }
`;

export default function FootageModal({
    show,
    setShowFootage,
    footageMonitor,
    roughStartDate,
    cameraOffsetMinutes,
    setFootageCameraOffsetMinutes,
    cap,
    props
}) {
    const [buttonSize, setButtonSize] = useState("large");
    const [footageDate, setFootageDate] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [project, setProject] = useState("");
    const [errors, setErrors] = useState(null);
    const [cams, setCams] = useState(null);
    const [urls, setUrls] = useState(null);
    const [selectedMonitor,setSelectedMonitor] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [requestErrors, setRequestErrors] = useState(null);
    const [transfer, setTransfer] = useState(null);

    useEffect(() => {
        if(window.innerWidth<767) {
            setButtonSize("small");
        }
    },[]);

    useEffect(() => {
        if(roughStartDate) {
            let local = new Date(roughStartDate);
            if(typeof roughStartDate === 'string') {
                local = new Date(roughStartDate.substring(0,roughStartDate.length - 1));
            }
            setFootageDate(local);
            setStartTime(local);
            const defaultEnd = addMinutesToDate(local, 5);
            const latestAllowed = getLatestAllowedFootageEndTimeWithOffset(cameraOffsetMinutes);
            setEndTime(defaultEnd > latestAllowed ? latestAllowed : defaultEnd);
        }
    },[roughStartDate, cameraOffsetMinutes]);

    useEffect(() => {
        if(footageMonitor && cams) {
            const g = getMonitorsGroupById(cams,footageMonitor);
            setSelectedMonitor(g.high.monitorId);
        }
    },[footageMonitor,cams])

    /** Returns the end time from the pickers as a UTC moment for comparison with latestAllowed. When cameraOffsetMinutes is set, picker time is camera local and is converted to UTC. */
    function getFullEndDateTime() {
        if (!footageDate || !endTime) return null;
        const y = footageDate.getFullYear();
        const m = footageDate.getMonth();
        const d = footageDate.getDate();
        const hr = endTime.getHours();
        const min = endTime.getMinutes();
        const sec = endTime.getSeconds();
        if (cameraOffsetMinutes != null) {
            // Picker time is in camera local (camera is cameraOffsetMinutes behind UTC); convert to UTC
            const base = new Date(Date.UTC(y, m, d, 0, 0, 0));
            base.setUTCMinutes(base.getUTCMinutes() + (hr * 60 + min) + cameraOffsetMinutes);
            base.setUTCSeconds(sec);
            return base;
        }
        // No camera TZ: treat picker time as user local
        return new Date(y, m, d, hr, min, sec);
    }

    useEffect(() => {
        if(startTime && endTime) {
            if (startTime > endTime) {
                setErrors("Make sure the end time is after the start time");
            } else if (addHoursToDate(startTime,2) < endTime) {
                setErrors("You can only request 2 hours of footage at a time");
            } else if(cap && !['ONLINE'].includes(cap.health)) {
                setErrors("System is currently not online, request cannot be placed");
            } else {
                const fullEnd = getFullEndDateTime();
                const latestAllowed = getLatestAllowedFootageEndTimeWithOffset(cameraOffsetMinutes);
                if (fullEnd && fullEnd > latestAllowed) {
                    setErrors(`End time must be at least ${FOOTAGE_BUFFER_MINUTES} minutes ago. Cameras have not yet written this footage to disk.`);
                } else {
                    setErrors(null);
                }
            }
        }
    },[startTime, endTime, footageDate, cameraOffsetMinutes]);

    useEffect(() => {
        if(errors) {
            console.log(startTime,endTime);
        }
    },[errors]);

    useEffect(() => {
        async function getStoredImages(capId) {
            const tempCams = await getMonitors(capId);
            setCams(tempCams);
            const tempSavedUrls = await getLatestStoredImages(capId);
            setUrls(tempSavedUrls);
        }
        if(cap && cap.capId) {
            getStoredImages(cap.capId);
        }
    },[cap]);

    /** Returns monitor ZM IDs to try in order (2CAM units may use 11/12 or 9/10). */
    function getMonitorZmIdCandidates() {
        const m = filterMonitorsByMonitorId(cams, selectedMonitor);
        if (cap.architecture.includes("2CAM")) {
            if (m.name === "House_Ultra_Res") {
                return ["11", "9"];
            } else if (m.name === "Neighbor_Ultra_Res") {
                return ["12", "10"];
            }
        } else if (cap.architecture.includes("4CAM")) {
            if (m.name === "House_Ultra_Res") {
                return ["5"];
            } else if (m.name === "Neighbor_Ultra_Res") {
                return ["6"];
            } else if (m.name === "Street_Ultra_Res") {
                return ["7"];
            } else if (m.name === "Driveway_Ultra_Res") {
                return ["8"];
            }
        }
        console.log("monitor selection does not make sense?", m);
        return [];
    }

    function calculateDate(start=true) {
        let pickTime = endTime;
        if(start) {
            pickTime = startTime;
        }
        // Move into local time
        pickTime = new Date(pickTime.getTime() - pickTime.getTimezoneOffset()*60000);
        const year = footageDate.getFullYear();
        const month = footageDate.getMonth();
        const day = footageDate.getDate();
        const hours = pickTime.getHours();
        const minutes = pickTime.getMinutes();
        const seconds = 0;
        return (new Date(year,month,day,hours,minutes,seconds)).toISOString().substring(0,19).replace('T',' ');
    }

    async function makeRequest() {
        setIsLoading(true);
        setRequestErrors(null);
        if(!cap || !cap.capId || !getUser().userId || !selectedMonitor) {
            setRequestErrors("Please make sure everything above is correct");
        } else {
            setRequestErrors(null);
            const monitorZmIdCandidates = getMonitorZmIdCandidates();
            if (monitorZmIdCandidates.length === 0) {
                setRequestErrors("Unable to find a view that makes sense");
            } else {
                const baseRequest = {
                    capId: cap.capId,
                    userId: getUser().userId,
                    project: project,
                    startDate: calculateDate(true),
                    endDate: calculateDate(false),
                };
                for (let i = 0; i < monitorZmIdCandidates.length; i++) {
                    const request = { ...baseRequest, monitorZmId: monitorZmIdCandidates[i] };
                    try {
                        console.log(request);
                        const tempTransfer = await makeTransferRequest(request);
                        console.log(tempTransfer);
                        setTransfer(tempTransfer);
                        break;
                    } catch (e) {
                        console.log(e);
                        if (i === monitorZmIdCandidates.length - 1) {
                            setRequestErrors("Request failed, please try again");
                        }
                    }
                }
            }
        }
        setIsLoading(false);
    }

    async function closeModal() {
        setTransfer(null);
        if (setFootageCameraOffsetMinutes) setFootageCameraOffsetMinutes(null);
        setShowFootage(false);
    }

    function calculateDisabled() {
        if(cap && cap.health==="ONLINE" && selectedMonitor && project && errors==null) {
            return false;
        }
        return true;
    }

    function renderImg(camera) {
        const mId = camera.medium.monitorId;

        const calculateClass = () => {
            if(selectedMonitor===camera.high.monitorId) {
                if(buttonSize==="small") {
                    return "selected-mobile";
                } else {
                    return "selected";
                }
            } else {
                if(buttonSize==="small") {
                    return "unselected-mobile";
                } else {
                    return "unselected";
                }
            }
        }
        
        if(urls && mId && urls[mId] && camera.medium.width>0) {
            return (
                <div className={calculateClass()} onClick={() => setSelectedMonitor(camera.high.monitorId)}>
                    <img 
                        style={{maxWidth:buttonSize==="small"?297:462}}
                        src={urls[mId].url}
                        alt="Loading Views..."
                        className="selected-image"
                    />
                </div>
            );
        }
    }

    return (
        <Modal
            show={show}
            onHide={() => closeModal()}
            container={this}
            aria-labelledby="contained-modal-title"
            style={{paddingLeft:"0px"}}
            className="footage-modal"
            dialogClassName={buttonSize==="small"?"modal-dialog-phone":"modal-dialog"}
            backdrop={true}
            enforceFocus={false}
        >
            <Modal.Header closeButton>
                <div className="footage-modal-title">Footage Request</div>
            </Modal.Header>
            <Modal.Body>
                {cap && (<div className="footage-modal-help">
                    <div>Please fill out the information below to request footage from <i>{cap.name}</i></div><br/ >
                    <ul>
                        <li>Requests may slow down live access to your system.</li>
                        <li>We will email you with a link to the footage when this completes.</li>
                        <li>Requests should take no more than 24 hours if the system is online.</li>
                        <li>Requests usually take less than 1 hour depending on how much footage you request.</li>
                        <li>The footage may include several minutes before your start time up until several minutes after your end time.</li>
                    </ul>
                </div>)}
                {transfer ? (
                    <View style={{flex:1,flexDirection:"column"}}>
                        <div style={{fontSize:"24px",textAlign:"center",paddingBottom:"40px"}}>
                            Wait time is expected to be {Math.floor(transfer.waitTime/60.)+10} minutes.<br /><br />
                            You will receive an email with a link to the footage once it's complete.
                        </div>
                        <Button
                            className="btn-brand-dark"
                            bsSize={buttonSize}
                            onClick={() => closeModal()}
                        >OK</Button>
                    </View>
                ) : (
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <View style={{flex:1,flexDirection:"column"}}>
                            <FormGroup>
                                <View style={{flex:1,flexDirection:"row"}}>
                                    <ControlLabel style={{width:"50%"}}>
                                        Name this Request
                                    </ControlLabel>
                                    <FormControl
                                        autoFocus
                                        value={project}
                                        onChange={e => setProject(e.target.value)}
                                        style={{maxWidth:"303px",height:"40.5px"}}
                                    />
                                </View>
                            </FormGroup>
                            <FormGroup>
                                <View style={{flex:1,flexDirection:"row"}}>
                                    <ControlLabel style={{width:"50%",paddingRight:"10px"}}>
                                        Date
                                    </ControlLabel>
                                    <CustomDatePicker
                                        format="MMM d"
                                        value={footageDate}
                                        onChange={setFootageDate}
                                        minDate={addDaysToDate(new Date(),-14)}
                                        InputProps={{disableUnderline:true}}
                                        slot={{dialog:{disableEnforceFocus:true}}}
                                    />
                                </View>
                            </FormGroup>
                            <FormGroup>
                                <View style={{flex:1,flexDirection:"row"}}>
                                    <ControlLabel style={{width:"50%"}}>
                                        Start Time
                                    </ControlLabel>
                                    <CustomTimePicker
                                        // format="H:mm"
                                        value={startTime}
                                        onChange={setStartTime}
                                        InputProps={{disableUnderline:true}}
                                    />
                                </View>
                            </FormGroup>
                            <FormGroup>
                                <View style={{flex:1,flexDirection:"row"}}>
                                    <ControlLabel style={{width:"50%"}}>
                                        End Time
                                    </ControlLabel>
                                    <CustomTimePicker
                                        // format=":mm"
                                        value={endTime}
                                        onChange={setEndTime}
                                        maxTime={(() => {
                                            if (!footageDate) return undefined;
                                            const latestAllowed = getLatestAllowedFootageEndTimeWithOffset(cameraOffsetMinutes);
                                            const isSameDay = footageDate.getFullYear() === latestAllowed.getFullYear() &&
                                                footageDate.getMonth() === latestAllowed.getMonth() &&
                                                footageDate.getDate() === latestAllowed.getDate();
                                            return isSameDay ? latestAllowed : undefined;
                                        })()}
                                        InputProps={{disableUnderline:true}}
                                    />
                                </View>
                            </FormGroup>
                            <div className="footage-modal-error">
                                {errors}
                            </div>
                            {cams && (
                                <>
                                <View style={{flex:1,flexDirection:"row",justifyContent:"center",textAlign:"center"}}>
                                    <ControlLabel>
                                        Select the camera view from which you need footage
                                    </ControlLabel>
                                </View>
                                <View style={{flex:1, flexDirection:"column",justifyContent:"center"}}>
                                    <View style={{flex:1, flexDirection:"row",justifyContent:"center"}}>
                                        {renderImg(cams.house)}
                                        {renderImg(cams.neighbor)}
                                    </View>
                                    {cap && !cap.architecture.includes("2CAM") && (
                                        <View style={{flex:1,flexDirection:"row",justifyContent:"center"}}>
                                            {renderImg(cams.street)}
                                            {renderImg(cams.driveway)}
                                        </View>
                                    )}
                                </View>
                                </>
                            )}
                            <div className="footage-modal-error">
                                {requestErrors}
                                {requestErrors && (
                                    <div className="footage-modal-error-contact">
                                        If this problem persists, please contact Bedrock Wireless: <a href="mailto:help@bedrockwireless.com">help@bedrockwireless.com</a>
                                    </div>
                                )}
                            </div>
                            <View style={{flex:1,flexDirection:"row",paddingTop:"10px"}}>
                                <View style={{width:"50%",paddingLeft:"10px",paddingRight:"10px"}}>
                                    <Button
                                        className="btn-brand-outline"
                                        bsSize={buttonSize}
                                        onClick={() => closeModal()}
                                        block
                                    >
                                        Cancel
                                    </Button>
                                </View>
                                <View style={{width:"50%",paddingLeft:"10px",paddingRight:"10px"}}>
                                    <LoaderButton
                                        className="btn-brand-dark"
                                        bsSize={buttonSize}
                                        onClick={makeRequest}
                                        isLoading={isLoading}
                                        disabled={calculateDisabled()}
                                        block
                                    >
                                        Make Request
                                    </LoaderButton>
                                </View>
                            </View>
                        </View>
                    </MuiPickersUtilsProvider>
                )}
            </Modal.Body>
        </Modal>
    )
}